import React, { useContext, useEffect, useState } from 'react'
import {
  ButtonsDivFlush,
  CloseDiv,
  CloseImg,
  CreateButtonImage,
  ErrorMessage,
  HeaderDiv,
  TipText,
} from '../CreateAccessUserModal/style'
import {
  ActivateButtonDiv,
  Container,
  CreateButtonDiv,
  CreateButtonText,
  DangerButtonDiv,
  HeaderText,
  InputDiv,
  InputField,
  LabelCreate,
  ModalActionsColumn,
  SelectField,
} from './style'
import {
  UserStatus,
  type UpdateUserStatusDto,
} from '../../types/userStatus'
import ConfirmationModal from '../ModalConfirmation'
import cross from '../../assets/addStockModal/cross.svg'
import ModalContext from '../../contexts/ModalContext'
import { useProfileContext } from '../../contexts/AccessesContext'
import { Save } from 'lucide-react'
import axios from '../../config/axios'
import ApiService from '../../services/ApiService'
import UserAttachmentSection, { type AttachmentData } from '../UserAttachmentSection'

/** Corpo esperado pelo `PUT /backend/user/:id` (UpdateUserDto) */
export type UpdateUserDto = {
  email: string
  phone: string
  profileId: number
  name: string
}

export function buildUpdateUserDto(form: {
  name: string
  email: string
  phone: string
  profileId: string
}): UpdateUserDto {
  return {
    name: form.name.trim(),
    email: form.email.trim(),
    phone: form.phone.trim(),
    profileId: form.profileId ? Number(form.profileId) : 0,
  }
}

export async function updateAccessUser(
  userId: number,
  dto: UpdateUserDto,
  attachments: AttachmentData[],
): Promise<void> {
  const formData = new FormData()
  Object.entries(dto).forEach(([k, v]) => formData.append(k, String(v)))
  attachments.forEach((a) => {
    if (a.file) formData.append('files', a.file)
  })
  formData.append(
    'filesMetadata',
    JSON.stringify(
      attachments.map((a) => ({
        title: a.title,
        ...(a.id !== undefined ? { id: a.id } : {}),
      })),
    ),
  )
  await axios.put(
    `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/user/${userId}`,
    formData,
  )
}

export async function updateAccessUserStatus(
  userId: number,
  dto: UpdateUserStatusDto,
): Promise<void> {
  await ApiService.user.updateStatus(userId, dto)
}

export async function deactivateAccessUser(userId: number): Promise<void> {
  return updateAccessUserStatus(userId, { status: UserStatus.INACTIVE })
}

export async function activateAccessUser(userId: number): Promise<void> {
  return updateAccessUserStatus(userId, { status: UserStatus.ACTIVE })
}

const EditAccessUserModal: React.FC = () => {
  const {
    setModalActive,
    selectedAccessUser,
    refreshCallback,
  } = useContext(ModalContext)
  const { profiles } = useProfileContext()

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    profileId: '',
  })
  const [attachments, setAttachments] = useState<AttachmentData[]>([])
  const [error, setError] = useState('')
  const [click, setClick] = useState(false)
  const [statusConfirmAction, setStatusConfirmAction] = useState<
    'activate' | 'deactivate' | null
  >(null)

  useEffect(() => {
    if (!selectedAccessUser) return
    setFormData({
      name: selectedAccessUser.name || '',
      email: selectedAccessUser.email || '',
      phone: selectedAccessUser.phone || '',
      profileId:
        selectedAccessUser.profileId != null
          ? String(selectedAccessUser.profileId)
          : '',
    })
    setAttachments(
      (selectedAccessUser.attachments ?? []).map((a) => ({
        id: a.id,
        fileUrl: a.fileUrl,
        title: a.title,
        isEditing: false,
      })),
    )
    setError('')
    setClick(false)
    setStatusConfirmAction(null)
  }, [selectedAccessUser])

  const validateForm = () => {
    if (!formData.name || formData.name.length < 3) return false
    if (!formData.email || !formData.email.includes('@')) return false
    if (formData.phone.trim() && formData.phone.trim().length < 10) return false
    return true
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setError('')
  }

  const handleSave = async () => {
    if (!selectedAccessUser) return
    if (selectedAccessUser.status === UserStatus.INACTIVE) return
    if (!validateForm()) {
      setClick(true)
      return
    }

    const dto = buildUpdateUserDto(formData)

    try {
      await updateAccessUser(selectedAccessUser.id, dto, attachments)
      refreshCallback?.()
      setModalActive(false)
    } catch (error: unknown) {
      console.error('[EditAccessUserModal] Falha ao salvar', error)
      const err = error as { response?: { status?: number; data?: { message?: string | string[] } } }
      if (err.response?.status === 400) {
        const messages = err.response?.data?.message
        if (Array.isArray(messages)) {
          setError(messages.join(', '))
        } else if (typeof messages === 'string') {
          setError(messages)
        } else {
          setError('Dados inválidos. Verifique os campos.')
        }
      } else {
        setError('Não foi possível salvar. Tente novamente.')
      }
    }
  }

  const handleConfirmStatusChange = async () => {
    if (!selectedAccessUser || !statusConfirmAction) return
    try {
      if (statusConfirmAction === 'deactivate') {
        await deactivateAccessUser(selectedAccessUser.id)
      } else {
        await activateAccessUser(selectedAccessUser.id)
      }
      setStatusConfirmAction(null)
      refreshCallback?.()
      setModalActive(false)
    } catch (e) {
      console.error('[EditAccessUserModal] Falha ao alterar status do usuário', e)
      setError(
        statusConfirmAction === 'deactivate'
          ? 'Não foi possível desativar o usuário. Tente novamente.'
          : 'Não foi possível ativar o usuário. Tente novamente.',
      )
      setStatusConfirmAction(null)
    }
  }

  const isValid = validateForm()

  if (!selectedAccessUser) {
    return null
  }

  const isInactive = selectedAccessUser.status === UserStatus.INACTIVE

  return (
    <Container>
      <HeaderDiv>
        <HeaderText>Editar usuário</HeaderText>
        <CloseDiv onClick={() => setModalActive(false)}>
          <CloseImg src={cross} alt="Fechar" />
        </CloseDiv>
      </HeaderDiv>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <InputDiv>
        <LabelCreate>Nome completo *</LabelCreate>
        <InputField
          placeholder="Digite o nome completo"
          type="text"
          value={formData.name}
          disabled={isInactive}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleInputChange('name', e.target.value)
          }
        />
      </InputDiv>

      <InputDiv>
        <LabelCreate>Email</LabelCreate>
        <InputField
          placeholder="Digite o email"
          type="email"
          value={formData.email}
          disabled
        />
      </InputDiv>

      <InputDiv>
        <LabelCreate>Telefone</LabelCreate>
        <InputField
          placeholder="Digite o telefone"
          type="text"
          value={formData.phone}
          disabled={isInactive}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleInputChange('phone', e.target.value)
          }
        />
      </InputDiv>

      <InputDiv>
        <LabelCreate>Senha</LabelCreate>
        <InputField
          placeholder="Alteração indisponível por enquanto"
          type="password"
          value=""
          disabled
          autoComplete="new-password"
        />
      </InputDiv>

      <InputDiv>
        <LabelCreate>Perfil de acesso (opcional)</LabelCreate>
        <SelectField
          value={formData.profileId}
          disabled={isInactive}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            handleInputChange('profileId', e.target.value)
          }
        >
          <option value="">Sem perfil</option>
          {profiles.map((profile) => (
            <option key={profile.id} value={profile.id}>
              {profile.name}
            </option>
          ))}
        </SelectField>
      </InputDiv>

      <UserAttachmentSection
        attachments={attachments}
        setAttachments={setAttachments}
        disabled={isInactive}
      />

      <ModalActionsColumn>
        {isInactive ? (
          <ActivateButtonDiv
            type="button"
            onClick={() => setStatusConfirmAction('activate')}
          >
            Ativar usuário
          </ActivateButtonDiv>
        ) : (
          <DangerButtonDiv
            type="button"
            onClick={() => setStatusConfirmAction('deactivate')}
          >
            Desativar usuário
          </DangerButtonDiv>
        )}
        <ButtonsDivFlush>
          {!isInactive && !isValid && click && (
            <TipText>Preencha todos os campos obrigatórios corretamente</TipText>
          )}
          <CreateButtonDiv
            role="button"
            aria-disabled={isInactive}
            onClick={isInactive ? undefined : handleSave}
            style={
              isInactive
                ? {
                    opacity: 0.5,
                    cursor: 'not-allowed',
                    pointerEvents: 'none',
                  }
                : undefined
            }
          >
            <Save width={16} height={16} />
            <CreateButtonText>Salvar alterações</CreateButtonText>
          </CreateButtonDiv>
        </ButtonsDivFlush>
      </ModalActionsColumn>

      <ConfirmationModal
        isOpen={statusConfirmAction !== null}
        title={
          statusConfirmAction === 'activate'
            ? 'Ativar usuário'
            : 'Desativar usuário'
        }
        message={
          statusConfirmAction === 'activate' ? (
            <p style={{ margin: 0 }}>
              O usuário voltará a ter acesso conforme o perfil de acesso definido.
            </p>
          ) : (
            <p style={{ margin: 0 }}>
              Isso poderá ser revertido, mas o usuário não terá acesso até então.
            </p>
          )
        }
        actions={[
          {
            label: 'Cancelar',
            onClick: () => setStatusConfirmAction(null),
            variant: 'outline',
          },
          statusConfirmAction === 'activate'
            ? {
                label: 'Ativar',
                onClick: handleConfirmStatusChange,
                variant: 'primary' as const,
              }
            : {
                label: 'Desativar',
                onClick: handleConfirmStatusChange,
                variant: 'danger' as const,
              },
        ]}
      />
    </Container>
  )
}

export default EditAccessUserModal
